/**
 * Subscription Service
 * Manages user subscriptions and usage limits in Firestore
 *
 * Uses a "webhooks + self-healing" pattern:
 * - Primary: Firestore (fast reads, updated by webhooks)
 * - Fallback: Paddle API reconciliation when stale/problematic
 */

import { db, auth } from '../firebase/config';
import {
  doc,
  getDoc,
  getDocFromServer,
  updateDoc,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { PLANS, SUBSCRIPTION_STATUS, getUsageLimit, getVideoProcessingLimit, TRIAL_VIDEO_PROCESSING_LIMIT } from '../config/plans';

class SubscriptionService {
  constructor() {
    this._lastVerifyCall = 0;
    this._verifyMinInterval = 60000; // 60 seconds between verify calls
  }

  /**
   * Verify and reconcile subscription with Paddle (source of truth)
   * @param {string} reason - 'login', 'checkout', 'manual'
   * @returns {Object} Verification result
   */
  async verifySubscription(reason = 'login') {
    try {
      // Rate limit on client side too
      const now = Date.now();
      if (reason !== 'checkout' && now - this._lastVerifyCall < this._verifyMinInterval) {
        console.log('Skipping verify - too soon since last call');
        return { skipped: true, reason: 'rate_limited' };
      }
      this._lastVerifyCall = now;

      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }

      const idToken = await user.getIdToken();

      const response = await fetch('/api/verify-subscription', {
        method: 'POST',
        headers: {
          'X-Firebase-Token': idToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      });

      if (response.status === 429) {
        const data = await response.json();
        console.warn('Rate limited:', data.message);
        return { skipped: true, reason: 'rate_limited', retryAfter: data.retryAfter };
      }

      if (!response.ok) {
        throw new Error(`Verify failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('Verify subscription result:', result);
      return result;
    } catch (error) {
      console.error('Error verifying subscription:', error);
      // Don't throw - verification is best-effort
      return { error: error.message };
    }
  }

  /**
   * Get user's current subscription status
   * @param {string} userId - Firebase user ID
   * @param {Object} options - { verify: boolean, verifyReason: string }
   * @returns {Object} Subscription data
   */
  async getUserSubscription(userId, options = {}) {
    const { verify = false, verifyReason = 'login' } = options;
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 300;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const userRef = doc(db, 'users', userId);

        // Force fetch from server to avoid stale cache data
        let userSnap;
        try {
          userSnap = await getDocFromServer(userRef);
        } catch (e) {
          console.warn('Server fetch failed, using cache:', e.message);
          userSnap = await getDoc(userRef);
        }

        if (!userSnap.exists()) {
          console.warn('User document does not exist for:', userId);
          return null;
        }

        let userData = userSnap.data();

        // If subscription status is missing, retry (Firestore might be syncing)
        if (!userData.subscriptionStatus && attempt < MAX_RETRIES - 1) {
          console.warn(`Subscription status missing, retrying (${attempt + 1}/${MAX_RETRIES})...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          continue;
        }

        console.log('Firestore userData:', {
          subscriptionStatus: userData.subscriptionStatus,
          subscriptionTier: userData.subscriptionTier,
          usageCount: userData.usageCount,
          usageLimit: userData.usageLimit,
        });

        // Only verify if API is likely available (skip in local dev without API running)
        const shouldVerify = (verify || this._shouldAutoVerify(userData));

        if (shouldVerify) {
          console.log('Triggering subscription verification...');
          const verifyResult = await this.verifySubscription(verifyReason);

          if (verifyResult && !verifyResult.error && !verifyResult.skipped) {
            // Verification succeeded — use the result
            if (verifyResult.reconciled && verifyResult.changed) {
              console.log('Subscription was updated, re-fetching...');
              userSnap = await getDocFromServer(userRef);
              userData = userSnap.data();
            } else if (verifyResult.entitlement) {
              userData = {
                ...userData,
                subscriptionStatus: verifyResult.entitlement.status,
                subscriptionTier: verifyResult.entitlement.tier,
                usageCount: verifyResult.entitlement.usageCount,
                usageLimit: verifyResult.entitlement.usageLimit,
              };
            }
          } else if (verify && verifyResult?.error) {
            // Caller explicitly requested verify (e.g. after checkout) and it failed — fail loudly
            console.error('Explicit subscription verification failed:', verifyResult.error);
            throw new Error(`Subscription verification failed: ${verifyResult.error}`);
          } else if (verifyResult?.error) {
            // Auto-verify failed — warn but fall back to Firestore data
            console.warn('Auto-verify failed, using cached Firestore data:', verifyResult.error);
          }
          // If skipped (rate limited), continue with Firestore data silently
        }

        // Check if trial has expired (local check)
        if (userData.subscriptionStatus === SUBSCRIPTION_STATUS.TRIAL) {
          const trialEndDate = userData.trialEndDate?.toDate?.() || new Date(userData.trialEndDate);
          const now = new Date();

          if (trialEndDate && now > trialEndDate) {
            // Try to update the status in Firestore, but don't fail if it errors
            try {
              await updateDoc(userRef, {
                subscriptionStatus: SUBSCRIPTION_STATUS.EXPIRED,
                updatedAt: serverTimestamp(),
              });
            } catch (updateError) {
              console.warn('Failed to update expired trial status in Firestore:', updateError.message);
              // Continue anyway - we'll treat it as expired locally
            }
            userData.subscriptionStatus = SUBSCRIPTION_STATUS.EXPIRED;
          }
        }

        return this._formatSubscription(userData);
      } catch (error) {
        console.error('Error getting user subscription:', error);
        if (attempt === MAX_RETRIES - 1) {
          throw error;
        }
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      }
    }
  }

  /**
   * Determine if we should auto-verify based on current state
   */
  _shouldAutoVerify(userData) {
    const status = userData.subscriptionStatus;

    // Problematic statuses should trigger verification
    const problematicStatuses = ['past_due', 'paused', 'cancelled', 'cancelling', 'expired'];
    if (problematicStatuses.includes(status) && userData.paddleCustomerId) {
      const updatedAt = userData.updatedAt?.toDate?.() || new Date(userData.updatedAt);
      const ageMinutes = (Date.now() - updatedAt.getTime()) / (1000 * 60);
      // If problematic and older than 10 minutes, verify
      if (ageMinutes > 10) {
        return true;
      }
    }

    return false;
  }

  /**
   * Format subscription data for frontend use
   */
  _formatSubscription(userData) {
    const isActive = this._isSubscriptionActive(userData);
    const canUse = this._canUseService(userData);

    console.log('Subscription check:', {
      status: userData.subscriptionStatus,
      isActive,
      canUse,
      usageCount: userData.usageCount,
      usageLimit: userData.usageLimit,
    });

    // Video processing limit: Firestore (allows override) else plan default from plans.js
    const videoProcessingCount = userData.videoProcessingCount || 0;
    const videoProcessingLimit = userData.videoProcessingLimit ??
      getVideoProcessingLimit(userData.subscriptionTier) ??
      TRIAL_VIDEO_PROCESSING_LIMIT;

    return {
      userId: userData.uid,
      status: userData.subscriptionStatus,
      tier: userData.subscriptionTier,
      usageCount: userData.usageCount || 0,
      usageLimit: userData.usageLimit,
      trialEndDate: userData.trialEndDate?.toDate?.() || userData.trialEndDate,
      subscriptionEndDate: userData.subscriptionEndDate?.toDate?.() || userData.subscriptionEndDate,
      paddleSubscriptionId: userData.paddleSubscriptionId,
      paddleCustomerId: userData.paddleCustomerId,
      isActive,
      canUseService: canUse,
      // Video processing tracking
      videoProcessingCount,
      videoProcessingLimit,
      videoProcessingRemaining: videoProcessingLimit !== null && videoProcessingLimit !== undefined
        ? Math.max(0, videoProcessingLimit - videoProcessingCount)
        : null,
    };
  }

  /**
   * Check if subscription is active
   */
  _isSubscriptionActive(userData) {
    const status = userData.subscriptionStatus;

    if (status === SUBSCRIPTION_STATUS.TRIAL) {
      const trialEndDate = userData.trialEndDate?.toDate?.() || new Date(userData.trialEndDate);
      return trialEndDate && new Date() <= trialEndDate;
    }

    return status === SUBSCRIPTION_STATUS.ACTIVE;
  }

  /**
   * Check if user can use the service
   */
  _canUseService(userData) {
    // Must have active subscription
    if (!this._isSubscriptionActive(userData)) {
      return false;
    }

    // If no usage limit (unlimited plan or trial), allow
    if (!userData.usageLimit) {
      return true;
    }

    // Check usage limit
    const usageCount = userData.usageCount || 0;
    return usageCount < userData.usageLimit;
  }

  /**
   * Increment usage count
   * @param {string} userId - Firebase user ID
   * @param {number} count - Number to increment by (default 1)
   */
  async incrementUsage(userId, count = 1) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        throw new Error('User not found');
      }

      const userData = userSnap.data();

      // Check if user can use the service
      if (!this._canUseService(userData)) {
        throw new Error('Usage limit exceeded or subscription inactive');
      }

      // Increment usage
      await updateDoc(userRef, {
        usageCount: increment(count),
        lastUsedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newCount = (userData.usageCount || 0) + count;

      return {
        success: true,
        usageCount: newCount,
        usageLimit: userData.usageLimit,
        remaining: userData.usageLimit ? userData.usageLimit - newCount : null,
      };
    } catch (error) {
      console.error('Error incrementing usage:', error);
      throw error;
    }
  }

  /**
   * Increment video processing count
   * @param {string} userId - Firebase user ID
   * @returns {Object} Result with success status and updated counts
   */
  async incrementVideoProcessingUsage(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        throw new Error('User not found');
      }

      const userData = userSnap.data();

      // Check if user can use the service
      if (!this._isSubscriptionActive(userData)) {
        throw new Error('Subscription inactive');
      }

      // Video processing limit: Firestore else plan default from plans.js
      const videoProcessingCount = userData.videoProcessingCount || 0;
      const videoProcessingLimit = userData.videoProcessingLimit ??
        getVideoProcessingLimit(userData.subscriptionTier) ??
        TRIAL_VIDEO_PROCESSING_LIMIT;

      // Check if limit exceeded (null = unlimited)
      if (videoProcessingLimit !== null && videoProcessingCount >= videoProcessingLimit) {
        throw new Error('Video processing limit exceeded');
      }

      // Increment video processing count
      await updateDoc(userRef, {
        videoProcessingCount: increment(1),
        lastVideoProcessingAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newCount = videoProcessingCount + 1;

      return {
        success: true,
        videoProcessingCount: newCount,
        videoProcessingLimit,
        remaining: videoProcessingLimit ? videoProcessingLimit - newCount : null,
      };
    } catch (error) {
      console.error('Error incrementing video processing usage:', error);
      throw error;
    }
  }

  /**
   * Get video processing stats
   * @param {string} userId - Firebase user ID
   */
  async getVideoProcessingStats(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        return null;
      }

      const userData = userSnap.data();
      const videoProcessingCount = userData.videoProcessingCount || 0;
      const videoProcessingLimit = userData.videoProcessingLimit ??
        getVideoProcessingLimit(userData.subscriptionTier) ??
        TRIAL_VIDEO_PROCESSING_LIMIT;

      return {
        current: videoProcessingCount,
        limit: videoProcessingLimit,
        remaining: Math.max(0, videoProcessingLimit - videoProcessingCount),
        isUnlimited: false,
      };
    } catch (error) {
      console.error('Error getting video processing stats:', error);
      return null;
    }
  }

  /**
   * Get usage statistics
   * @param {string} userId - Firebase user ID
   */
  async getUsageStats(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        return null;
      }

      const userData = userSnap.data();
      const usageCount = userData.usageCount || 0;
      const usageLimit = userData.usageLimit;

      return {
        current: usageCount,
        limit: usageLimit,
        remaining: usageLimit ? Math.max(0, usageLimit - usageCount) : null,
        percentage: usageLimit ? Math.min(100, (usageCount / usageLimit) * 100) : 0,
        isUnlimited: !usageLimit,
      };
    } catch (error) {
      console.error('Error getting usage stats:', error);
      return null;
    }
  }

  /**
   * Get plan details
   */
  getPlan(planId) {
    return PLANS[planId] || null;
  }

  /**
   * Get all available plans
   */
  getAllPlans() {
    return Object.values(PLANS);
  }

  /**
   * Check what upgrade reason applies (if any)
   * @param {Object} subscription - Subscription object from getUserSubscription
   */
  getUpgradeReason(subscription) {
    if (!subscription) return 'no_subscription';

    if (subscription.status === SUBSCRIPTION_STATUS.TRIAL) {
      const trialEnd = subscription.trialEndDate instanceof Date
        ? subscription.trialEndDate
        : new Date(subscription.trialEndDate);
      if (new Date() > trialEnd) return 'trial_expired';
    }

    if (subscription.status === SUBSCRIPTION_STATUS.EXPIRED) return 'subscription_expired';
    if (subscription.status === SUBSCRIPTION_STATUS.CANCELLED) return 'subscription_cancelled';

    if (!subscription.canUseService && subscription.usageLimit) {
      return 'limit_reached';
    }

    return null;
  }
}

// Export singleton instance
export const subscriptionService = new SubscriptionService();

export default subscriptionService;
